/* UTILS */
import { computed } from 'vue';

import ViktorClient from '@/api/clients/viktorClient/ViktorClient';
import { DEFAULT_PER_PAGE } from '@/api/constants';
/* STORES */
import { useAuthStore } from '@/stores/auth';
import type { PaginatedResponse } from '@/types/api/axios';
import type { SourcingHistoryResponse } from '@/types/api/sourcing/sourcing';
/* TYPES */
import type { Affinity, TUserManagerFit } from '@/types/api/user/affinity/affinity';
import type { PredictData } from '@/types/api/user/predict-success/predictSuccess';
import type { AssessmentDataV2 } from '@/types/api/user/synthesis/assessmentData';
import type { PoleEmploi } from '@/types/api/user/synthesis/synthesisPoleEmploi';
import type { SynthesisIntro } from '@/types/api/user/synthesis/userSynthesis';
import type { GetUser, PatchUserParams, PatchUserResponse } from '@/types/api/user/user';
import type { BatchCallData, BatchResponse } from '@/types/batches/batches.types';
import type { BaseSharepool } from '@/types/sharepools/sharepools.types';
import type { UserData, UserPersonalityTraits } from '@/types/users/users';

/* API */
import { getRecruiterContactsBatchQuery, postBatch } from '../batches/batches.api';
import { getStepsUsersBatchQuery } from '../campaign/campaign.api';
import type { CampaignUsersQueryParams } from '../campaign/campaign.types';
import type { ContactsQueryParams } from '../contacts/contacts.types';
import type { SkillType } from '../skill/skill.types';
import type {
  InviteContactParams,
  InviteContactResponse,
  UserGroup,
  UserMotivations,
  UserPersonality,
  UserPmHistory,
  UserProfileSkill,
  UserRolesResponse,
  UserSkill,
  UserValues,
  UserWorkflow,
  WorkflowReminderResult
} from './user.types';

/**
 * Get all workflows for a given user
 * @param {String} uuid - uuid of user to retrieve workflows for
 * @returns {Promise<UserWorkflow[]>} - promise with user workflows
 */
export async function getUserWorkflows(uuid: string): Promise<PaginatedResponse<UserWorkflow[]>> {
  const params = {
    per_page: 0
  };
  return ViktorClient().get(`/user/${uuid}/workflows`, { params });
}

/**
 *Get a given user synthesis intro text
 * @param {String} uuid - uuid of user to retrieve synthesis intro for
 * @returns {Promise<SynthesisIntro>} - promise with synthesis intro data
 */
export function getSynthesisIntro(uuid): Promise<SynthesisIntro> {
  return ViktorClient().get(`/user/${uuid}/synthesis-intro`);
}

/**
 *Get a given user pole emploi synthesis data
 * @param {String} uuid - uuid of user to retrieve synthesis for
 * @returns {Promise<PoleEmploi>} - promise with synthesis data
 */
export function getSynthesisPoleEmploi(uuid): Promise<PoleEmploi> {
  return ViktorClient().get(`/user/${uuid}/synthesis-pole-emploi`);
}

/**
 *Get a given user assessment data
 * @param {String} uuid - uuid of user to retrieve synthesis for
 * @returns {Promise<AssessmentData>} - promise with assessment data
 */
export function getAssessmentSummary(uuid): Promise<AssessmentDataV2> {
  return ViktorClient().get(`/v2/user/${uuid}/assessments-summary`);
}

/**
 * Get predict success data V2 for a given user and pm
 * @param data
 * @param {String} data.uuid - uuid of user for which to calculate predict
 * @param {String} data.pmUuid - uuid of pm for which to calculate predict
 * @returns {Promise<PredictData & { userUuid: string}>} - promise with predict success data
 */
export async function getPredictSuccess({ uuid, pmUuid }): Promise<PredictData & { userUuid: string }> {
  const response = await ViktorClient().get(`/v2/user/${uuid}/pm/${pmUuid}/predict-success`);
  return { ...response, userUuid: uuid };
}

/**
 * Get affinity data for two given users
 * @param data
 * @param {String} data.uuid - uuid of first user to check affinity for
 * @param {String} data.comparedContactUuid - uuid of second user to check affinity for
 * @returns {Promise<Affinity>} - promise with affinity data
 */
export function getAffinity({ uuid, comparedContactUuid }): Promise<Affinity> {
  return ViktorClient().get(`/user/${uuid}/affinity/compared/${comparedContactUuid}`);
}

/**
 * Get affinity data for a given user against a list of candidates (e.g., from a group)
 * @param data
 * @param {String} data.userUuid - uuid of the main user
 * @param {Array<String>} data.candidateUuids - array of UUIDs of candidates to compare against
 * @returns {Promise<Affinity>} - promise with affinity data
 */
export function getAffinityForCandidatesList({ userUuid, candidateUuids }): Promise<Affinity> {
  const params = {
    candidate_uuid: candidateUuids
  };
  return ViktorClient().get(`/user/${userUuid}/affinity`, { params });
}

/**
 * Get a given user info
 * @param uuid
 * @returns {Promise<GetUser>} - promise of user's data
 */
export function getUser(
  uuid: string,
  fields: string[] = ['aplayer_score'],
  options?: { getPoleEmploi?: boolean }
): Promise<GetUser> {
  const params = {
    fields
  };
  if (options?.getPoleEmploi) {
    params['pole_emploi'] = 1;
  }

  return ViktorClient().get(`/user/${uuid}`, { params });
}

/**
 * Get invitation history for a given candidate
 * @param {String} uuid - candidate's uuid
 *
 * @returns {SourcingHistoryResponse} - history
 */
export function getCandidateHistory(uuid: string): Promise<SourcingHistoryResponse> {
  const sharepoolSpace = computed(() => useAuthStore().getSharepoolSpace);

  // remove pagination
  const params = {
    per_page: 0,
    fields: 'target.deleted_at',
    sharepool_uuid: sharepoolSpace.value
  };

  return ViktorClient().get(`/user/${uuid}/sourcing/history`, { params });
}

// Get groups a user is part of
export function getUserGroups(uuid, { page = 1, perPage = DEFAULT_PER_PAGE }): Promise<PaginatedResponse<UserGroup[]>> {
  const sharepoolSpace = computed(() => useAuthStore().getSharepoolSpace);
  const params = {
    page,
    with_candidates: 1,
    per_page: perPage,
    order: 'label:asc',
    fields: 'sharepool_uuid,uuid',
    sharepool_uuid: sharepoolSpace.value
  };

  return ViktorClient().get(`/user/${uuid}/groups`, { params });
}
/**
 * Get recently viewed pms for the recruiter
 * @returns {Promise<PaginatedResponse<UserPmHistory[]>>} - history of pms for recruiter ordered by recently viewed
 */
export function getUserPmHistory({
  has_skills = undefined
}: {
  has_skills?: boolean;
} = {}): Promise<PaginatedResponse<UserPmHistory[]>> {
  const authUuid = computed(() => useAuthStore().getFullUser.uuid);
  const params = { per_page: 10, has_skills };

  return ViktorClient().get(`/user/${authUuid.value}/predictive-models/history`, { params });
}

/**
 * Get the user's skills (voice and selfdeclared)
 */
export function getUserSkills(userUuid, type: SkillType | undefined = undefined): Promise<UserSkill[]> {
  const params = {
    type: type || undefined,
    per_page: 0
  };
  return ViktorClient().get(`/user/${userUuid}/skills`, { params });
}

/**
 * Get the user's skills for candidate profile
 */
export function getUserProfileSkills(userUuid): Promise<UserProfileSkill[]> {
  const params = {
    per_page: 0
  };
  return ViktorClient().get(`/user/${userUuid}/profile/skills`, { params });
}

/* POST */

/**
 * Request a user to pass voice for a pm
 *
 */
export function postRequestUserPmVoice(userUuid, pmUuid): Promise<void> {
  return ViktorClient().post(`/user/${userUuid}/pm/${pmUuid}/voice`);
}

/**
 * Send reminder (invite or test) to selected user
 *
 */

export async function postUserWorkflowReminder(
  uuids: string[],
  type: 'network' | 'campaign' = 'network',
  globalParams:
    | ContactsQueryParams
    | (Partial<CampaignUsersQueryParams> & { step_uuid: string; campaign_uuid: string })
    | undefined = undefined,
  targetUuid?: string,
  sourceUuid?: string
): Promise<BatchResponse<WorkflowReminderResult>> {
  const params: { target_uuid?: string; source_uuid?: string } = {};
  if (sourceUuid) params.source_uuid = sourceUuid;
  else if (targetUuid) params.target_uuid = targetUuid;
  const effectiveSourceUuid = sourceUuid ?? targetUuid;
  const body = effectiveSourceUuid ? { source_uuid: effectiveSourceUuid } : {};

  if (globalParams) {
    // With campaign
    // GLOBAL MODE
    const campaignUuid = 'campaign_uuid' in globalParams ? globalParams.campaign_uuid : '';
    return postBatch({
      query:
        type === 'network'
          ? getRecruiterContactsBatchQuery(globalParams as ContactsQueryParams)
          : getStepsUsersBatchQuery(campaignUuid, globalParams as CampaignUsersQueryParams & { step_uuid: string }),
      action: { method: 'POST', endpoint: '/viktor/user/:user_uuid/workflow-reminder', data: [{ body }] }
    });
  }
  if (uuids.length === 1) {
    // SINGLE MODE
    return ViktorClient().post(`/user/${uuids[0]}/workflow-reminder`, params);
  } else {
    // BULK MODE
    const data: BatchCallData[] = uuids.map(uuid => ({
      params: {
        user_uuid: uuid
      },
      body
    }));
    return postBatch({
      action: { method: 'POST', endpoint: '/viktor/user/:user_uuid/workflow-reminder', data }
    });
  }
}

/**
 * Send users results by email
 *
 * @param uuids - array of user Uuids
 * @param reportIds list of report ids to send
 * @param targetUuid - uuid of target for adequacy report if any
 *  @param globalParams - global params for batch if global mode
 *
 */
export function postUserResults(
  uuids: string[],
  reportIds,
  targetUuid,
  globalParams: ContactsQueryParams | undefined = undefined,
  customReport:
    | { report_custom_hr?: boolean; report_custom_business?: boolean; report_custom_client?: boolean }
    | undefined = undefined
): Promise<BatchResponse<void>> {
  const sharepoolSpace = computed(() => useAuthStore().getSharepoolSpace);
  const params = {
    reports_ids: reportIds,
    target_uuid: targetUuid || undefined,
    sharepool_uuid: sharepoolSpace.value,
    custom_reports: customReport
  };
  // GLOBAL MODE
  if (!uuids.length && globalParams) {
    const data = [{ params }];
    return postBatch({
      query: getRecruiterContactsBatchQuery(globalParams),
      action: { method: 'POST', endpoint: '/viktor/user/:user_uuid/results', data }
    });
  } else if (uuids.length === 1) {
    // SINGLE MODE
    return ViktorClient().post(`/user/${uuids[0]}/results`, params);
  } else {
    // BULK MODE
    const batchParams: BatchCallData[] = uuids.map(uuid => ({ params: { user_uuid: uuid }, body: params }));
    return postBatch({
      action: { method: 'POST', endpoint: '/viktor/user/:user_uuid/results', data: batchParams }
    });
  }
}

export function postUserRetakeTest(
  workflowUserUuid: string,
  assessments: string[]
): Promise<{ last_reminder_at: string }> {
  const params = {
    questionnaires: assessments
  };

  return ViktorClient().post(`/workflow-user/${workflowUserUuid}/retake`, params);
}

/**
 * Add one or more users to noe or more groups
 *
 * @param {string[]} userUuids Uuids of users to add to the group
 * @param {string[]} groupUuids uuids of groups to add users in
 * @param globalParams - global params for batch if global mode
 */
export async function postAddUsersToGroups(
  userUuids: string[],
  groupUuids: string[],
  globalParams: ContactsQueryParams | undefined = undefined
): Promise<BatchResponse<any>> {
  // GLOBAL MODE - if multiple groups, one batch per group
  if (!userUuids.length && globalParams) {
    const promises: Promise<any>[] = [];
    groupUuids.forEach(uuid => {
      const data = [{ params: { group_uuid: uuid } }];
      promises.push(
        postBatch({
          query: getRecruiterContactsBatchQuery(globalParams),
          action: { method: 'POST', endpoint: '/viktor/user/:user_uuid/group/:group_uuid', data }
        })
      );
    });

    const response = await Promise.allSettled(promises);
    return response.map(response => (response.status === 'rejected' ? response.reason : response.value));
  }
  // SINGLE MODE
  else if (userUuids.length === 1 && groupUuids.length === 1) {
    return ViktorClient().post(`/user/${userUuids[0]}/group/${groupUuids[0]}`);
  } else {
    // BULK MODE
    const data: BatchCallData[] = [];
    userUuids.forEach(uuid => {
      data.push(...groupUuids.map(groupUuid => ({ params: { user_uuid: uuid, group_uuid: groupUuid } })));
    });
    return postBatch({
      action: { method: 'POST', endpoint: '/viktor/user/:user_uuid/group/:group_uuid', data }
    });
  }
}

/**
 * Add a pm to the history of the current recruiter
 * @param pmUuid UUID of the PM
 * @returns {Promise<void>}
 *
 */
export function postUserPmHistory(pmUuid): Promise<void> {
  const uuid = useAuthStore().getFullUser.uuid;
  return ViktorClient().post(`/user/${uuid}/predictive-models/${pmUuid}/history`);
}

/* PATCH */
/**
 * Allows to update a user's data
 * @param {string} uuid - uuid of user to update
 * @param {Partial<PatchUserParams>} params params to send to the PATCH
 * @returns {Promise<PatchUserResponse>} the updated user data
 */
export function patchUser(uuid: string, params: Partial<PatchUserParams>): Promise<PatchUserResponse> {
  return ViktorClient().patch(`/user/${uuid}`, params);
}

/**
 * Patch user_data object on user
 * @param {string} userUuid Uuid of the user to update the data
 * @param {UserData} params Parameters to update for the user
 */
export function patchUserData(userUuid: string, params: UserData) {
  return ViktorClient().patch(`/user/${userUuid}/data`, params);
}

/**
 * Allows to get the sharepools of a user in common with the recruiter
 * @param {string} userUuid - uuid of user
 * @returns {Promise<Sharepool[]>}
 */
export const getUserSharepools = (userUuid): Promise<BaseSharepool[]> => {
  const recruiterUuid = useAuthStore().getFullUser.uuid;
  return ViktorClient().get(`/user/${userUuid}/recruiter/${recruiterUuid}/sharepools`);
};

/**
 *
 * @param userUUID uuid of the user/candidate
 * @param managerUUID uuid of the manager
 * @returns promise of user's manger fit datas
 * Use for old profile
 */
export function getUserManagerFit(userUUID: string, managerUUID: string): Promise<TUserManagerFit> {
  return ViktorClient().get(`/user/${userUUID}/fit/manager/${managerUUID}`);
}

/**
 *
 * @param managerUUID uuid of the manager
 * @param candidateUUIDs uuids of the candidates
 * @returns promise of user's manger fit datas
 * Use for new profile
 */
export function getUsersManagerFit(managerUUID: string, candidateUUIDs: string | string[]): Promise<any> {
  return ViktorClient().get(`/user/${managerUUID}/fit/manager`, {
    params: {
      candidate_uuid: candidateUUIDs
    }
  });
}

export function getUserPersonality(uuid: string): Promise<UserPersonality> {
  return ViktorClient().get(`/user/${uuid}/personality`);
}

export function getUserPersonalityTraits(uuid: string): Promise<UserPersonalityTraits> {
  return ViktorClient().get(`/user/${uuid}/personality/traits`);
}

export function getUserValues(uuid: string): Promise<UserValues> {
  return ViktorClient().get(`/user/${uuid}/values`);
}

export function getUserMotivations(uuid: string): Promise<UserMotivations> {
  return ViktorClient().get(`/user/${uuid}/values/motivations`);
}

export function getUserRolesFromEmail(email): Promise<UserRolesResponse> {
  const companyUuid = useAuthStore().getCompanyUuid;
  return ViktorClient().get(`/company/${companyUuid}/user/${email}`);
}

// INVITE
export function postInviteContact(payload: InviteContactParams): Promise<BatchResponse<InviteContactResponse>> {
  const apiData: Partial<InviteContactParams> = { ...payload };
  if (!apiData.message) {
    delete apiData.message;
  }
  delete apiData.emailsList;
  delete apiData.shouldShowAlreadyAccepted;
  delete apiData.showToaster;
  if (payload.emailsList.length === 1) {
    return ViktorClient().post('/user/invite-contact', { ...apiData, email: payload.emailsList[0] });
  } else {
    const batchParams = payload.emailsList.map(email => ({ body: { ...apiData, email } }));
    return postBatch({
      action: { method: 'POST', endpoint: '/viktor/user/invite-contact', data: batchParams }
    });
  }
}
